import React, { createContext, useContext, useState, useEffect, useRef } from 'react';

const ThemeContext = createContext();

// Default theme configurations based on UI Kit
const defaultThemes = {
  light: {
    name: 'Light',
    colors: {
      // Primary Colors from UI Kit
      primary: '#1D75BF',
      primaryGradient: 'linear-gradient(90deg, #1D75BF 0%, #5FAEF1 100%)',
      primaryLight: '#5FAEF1',
      
      // Secondary Colors from UI Kit
      secondary: '#666666',
      gray80: '#333333',
      gray60: '#666666',
      gray40: '#999999',
      gray20: '#CCCCCC',
      gray10: '#E5E5E5',
      white: '#FFFFFF',
      
      // Background Colors from UI Kit
      background: '#FAF8F8',
      surface: '#F5F7FA',
      sidebar: '#F5F7FA',
      lightDetails: '#FFFFFF',
      gray: '#F4F4F4',
      
      // Text Colors
      text: '#212121',
      textSecondary: '#ABABAB',
      darkDetails: '#212121',
      
      // Action Colors from UI Kit
      success: '#4AC478',
      danger: '#B60012',
      warning: '#F8AF58',
      warningDark: '#8A4C00',
      info: '#5FAEF1',
      
      // UI Elements
      border: '#E5E5E5',
      shadow: 'rgba(0, 0, 0, 0.1)',
      disabled: '#CCCCCC',
    },
  },
  dark: {
    name: 'Dark',
    colors: {
      // Primary Colors adapted for dark mode
      primary: '#5FAEF1',
      primaryGradient: 'linear-gradient(90deg, #1F2D3B 0%, #5FAEF1 100%)',
      primaryLight: '#5FAEF1',
      
      // Dark Mode Colors from UI Kit
      background: '#121212',
      surface: '#1A1A1A',
      surface2: '#1E1E1E',
      surface3: '#232323',
      sidebar: '#1A1A1A',
      
      // Text Colors for dark mode
      text: '#FFFFFF',
      textSecondary: '#ABABAB',
      darkDetails: '#FFFFFF',
      
      // Secondary Colors adapted for dark mode
      secondary: '#999999',
      gray80: '#E5E5E5',
      gray60: '#CCCCCC',
      gray40: '#999999',
      gray20: '#666666',
      gray10: '#333333',
      white: '#FFFFFF',
      
      // Action Colors adapted for dark mode
      success: '#4AC478',
      danger: '#B60012',
      warning: '#F8AF58',
      warningDark: '#8A4C00',
      info: '#5FAEF1',
      
      // UI Elements for dark mode
      border: '#2C2C2C',
      shadow: 'rgba(0, 0, 0, 0.3)',
      disabled: '#363636',
    },
  },
  custom: {
    name: 'Custom',
    colors: {
      // Default to light theme colors for custom
      primary: '#1D75BF',
      primaryGradient: 'linear-gradient(90deg, #1D75BF 0%, #5FAEF1 100%)',
      primaryLight: '#5FAEF1',
      secondary: '#666666',
      gray80: '#333333',
      gray60: '#666666',
      gray40: '#999999',
      gray20: '#CCCCCC',
      gray10: '#E5E5E5',
      white: '#FFFFFF',
      background: '#FAF8F8',
      surface: '#F5F7FA',
      sidebar: '#F5F7FA',
      lightDetails: '#FFFFFF',
      gray: '#F4F4F4',
      text: '#212121',
      textSecondary: '#ABABAB',
      darkDetails: '#212121',
      success: '#4AC478',
      danger: '#B60012',
      warning: '#F8AF58',
      warningDark: '#8A4C00',
      info: '#5FAEF1',
      border: '#E5E5E5',
      shadow: 'rgba(0, 0, 0, 0.1)',
      disabled: '#CCCCCC',
    },
  },
};

export const ThemeProvider = ({ children }) => {
  // Initialize theme from localStorage immediately (lazy initialization)
  const [currentTheme, setCurrentTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      const savedTheme = localStorage.getItem('admin-theme');
      return savedTheme || 'light';
    }
    return 'light';
  });
  
  // Initialize custom colors from localStorage immediately
  const [customColors, setCustomColors] = useState(() => {
    if (typeof window !== 'undefined') {
      const savedCustomColors = localStorage.getItem('admin-custom-colors');
      if (savedCustomColors) {
        try {
          return JSON.parse(savedCustomColors);
        } catch (error) {
          console.error('Error parsing saved custom colors:', error);
        }
      }
    }
    return defaultThemes.custom.colors;
  });
  
  const [systemTheme, setSystemTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      return mediaQuery.matches ? 'dark' : 'light';
    }
    return 'light';
  });

  // Track if this is the initial mount to avoid saving on first render
  const isInitialMount = useRef(true);

  // Detect system theme preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      setSystemTheme(e.matches ? 'dark' : 'light');
    };

    setSystemTheme(mediaQuery.matches ? 'dark' : 'light');
    mediaQuery.addEventListener('change', handleChange);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Mark initial mount as complete after first render
  useEffect(() => {
    isInitialMount.current = false;
  }, []);

  // Save theme to localStorage when it changes (but not on initial mount)
  useEffect(() => {
    // Don't save on initial mount - theme is already loaded from localStorage
    if (isInitialMount.current) {
      return;
    }
    
    // Save theme when user explicitly changes it
    localStorage.setItem('admin-theme', currentTheme);
  }, [currentTheme]);

  // Save custom colors to localStorage when they change
  useEffect(() => {
    if (currentTheme === 'custom') {
      localStorage.setItem('admin-custom-colors', JSON.stringify(customColors));
    }
  }, [customColors, currentTheme]);

  const getCurrentThemeConfig = () => {
    if (currentTheme === 'system') {
      return defaultThemes[systemTheme];
    }
    if (currentTheme === 'custom') {
      return { name: 'Custom', colors: customColors };
    }
    return defaultThemes[currentTheme] || defaultThemes.light;
  };

  const setTheme = (theme) => {
    setCurrentTheme(theme);
  };

  const updateCustomColors = (colors) => {
    setCustomColors(prev => ({ ...prev, ...colors }));
  };

  const resetCustomColors = () => {
    setCustomColors(defaultThemes.custom.colors);
  };

  const getThemeColors = () => {
    return getCurrentThemeConfig().colors;
  };

  const value = {
    currentTheme,
    systemTheme,
    customColors,
    setTheme,
    updateCustomColors,
    resetCustomColors,
    getCurrentThemeConfig,
    getThemeColors,
    availableThemes: Object.keys(defaultThemes).concat(['system']),
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
